/*
 * Copyright (c) 2023 Powerledger Pty Ltd. All Rights Reserved.
 */

import { Box, Button, Flex, IconDownload, IconLoading, Text, useResponsiveValue } from "@powerledger/ui-component-lib";
import { useTranslation } from "react-i18next";

import { SeparatorLayout } from "@/app/components/separator-layout";
import { formatDate } from "@/app/lib/format-date";
import { volumeFormatter } from "@/app/lib/volume-formatter";

import { TradingSummaryHeaderProps } from "./trading-summary-header.types";

export const TradingSummaryHeader: React.FC<TradingSummaryHeaderProps> = ({
  totalTradedVolume,
  startDate,
  endDate,
  loading,
  downloadReport,
}) => {
  const showSeparator = useResponsiveValue([false, false, true]);
  const { t } = useTranslation();

  return (
    <Flex
      sx={{
        mb: 3,
        bg: "#262547",
        borderRadius: "8px",
        p: 3,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {loading ? (
        <IconLoading />
      ) : (
        <>
          <Box
            sx={{
              width: "100%",
              color: "textDarker",
              fontSize: 0,
              flex: 2,
            }}
          >
            <SeparatorLayout
              reverse
              showSeparator={showSeparator}
              data={{
                "Start Date": formatDate(startDate, {
                  noTZ: true,
                  noOffset: true,
                }),
                "End Date": formatDate(endDate, {
                  noTZ: true,
                  noOffset: true,
                }),
                "Total Traded Volume": volumeFormatter(totalTradedVolume),
              }}
              headingStyle={{
                color: "primary",
              }}
              wrapperStyle={{
                pl: 3,
              }}
              contentStyle={{
                width: ["100%", "100%", "30%", "25%"],
              }}
            />
          </Box>
          {totalTradedVolume > 0 && (
            <Button
              type="button"
              sx={{
                position: "relative",
                padding: 0,
                bottom: "6px",
                height: "50px",
                background: "transparent",
                color: "white",
                "&:enabled:hover, &:enabled:focus": { background: "transparent " },
              }}
              onClick={downloadReport}
            >
              <IconDownload width={50} height={50} />
              <Text
                sx={{
                  position: "absolute",
                  bottom: "-5px",
                }}
              >
                {t("CSV")}
              </Text>
            </Button>
          )}
        </>
      )}
    </Flex>
  );
};
